/**
 * Widget V2 - Assessment View
 *
 * Sport level self-assessment form.
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";

/**
 * Assessment View component
 */
function AssessmentView() {
  const { sport: sportId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, sports, updateEligibility } = useWidget();
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({});

  // Get sport info
  const sportInfo = sports?.find((s) => s._id === sportId);

  // Fetch assessment config
  useEffect(() => {
    const fetchConfig = async () => {
      if (!slug || !sportId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await api.getAssessmentConfig(slug, sportId);
        setConfig(data);
      } catch (err) {
        setError(err.message || "Failed to load assessment");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [slug, sportId]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isAuthenticated) {
      enqueueSnackbar("Please sign in to complete assessment", { variant: "error" });
      navigate(`/${slug}/login?redirect=assessment/${sportId}`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.submitAssessment(slug, sportId, formData);

      // Update eligibility in context
      if (result.assignedLevel) {
        updateEligibility({
          [sportInfo?.name || sportId]: result.assignedLevel
        });
      }

      enqueueSnackbar("Assessment completed successfully!", { variant: "success" });
      navigate(`/${slug}`);
    } catch (err) {
      enqueueSnackbar(err.message || "Assessment submission failed", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form field change
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {sportInfo?.name || "Sport"} Assessment
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Complete this assessment to unlock booking for your skill level
      </Typography>

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Assessment form */}
      {!loading && !error && config && (
        <Card>
          <CardContent>
            {config.currentLevel && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You already have a level ({config.currentLevel}) for this sport. Completing this assessment will update your level.
              </Alert>
            )}

            <Typography variant="body2" color="textSecondary" mb={3}>
              Please answer the following questions honestly to help us determine your skill level.
            </Typography>

            {/* TODO: Implement dynamic form fields based on config.formFields */}
            <Box sx={{ mb: 3 }}>
              {config.formFields?.map((field) => (
                <Box key={field.name} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {field.label}
                    {field.required && " *"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {/* TODO: Render appropriate input based on field.type */}
                    Field type: {field.type}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button variant="contained" onClick={handleSubmit} disabled={submitting} fullWidth>
              {submitting ? "Submitting..." : "Complete Assessment"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Not authenticated notice */}
      {!isAuthenticated && !loading && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You need to sign in to complete the assessment.
          <Button size="small" onClick={() => navigate(`/${slug}/login?redirect=assessment/${sportId}`)} sx={{ ml: 1 }}>
            Sign In
          </Button>
        </Alert>
      )}
    </Box>
  );
}

export default AssessmentView;
